import { useEffect, useState } from "react";
import { Clock, RefreshCw, Search, MapPin, Facebook, Instagram, ChevronDown } from "lucide-react";
import { Card } from "@/components/kit";
import { useScrapeHistory } from "@/lib/useScrape";

const SOURCE_ICONS = {
  google_maps: { icon: MapPin, color: "text-sky-500", bg: "bg-sky-50" },
  facebook: { icon: Facebook, color: "text-[#1877F2]", bg: "bg-blue-50" },
  instagram: { icon: Instagram, color: "text-pink-500", bg: "bg-pink-50" },
};

const SOURCE_LABELS = {
  google_maps: "Google Maps",
  facebook: "Facebook",
  instagram: "Instagram",
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Baru saja";
  if (mins < 60) return `${mins}m lalu`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}j lalu`;
  const days = Math.floor(hours / 24);
  return `${days}h lalu`;
}

export default function ScrapeHistory({ source, onRerun }) {
  const { history, historyLoading, fetchHistory } = useScrapeHistory();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) fetchHistory(10);
  }, [open, fetchHistory]);

  const filtered = source ? history.filter((h) => h.source === source) : history;

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-sky-500 transition"
      >
        <Clock className="w-3.5 h-3.5" />
        Riwayat pencarian
        {filtered.length > 0 && (
          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{filtered.length}</span>
        )}
        <ChevronDown className={`w-3 h-3 ml-auto transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <Card className="mt-2 p-0 overflow-hidden animate-float-up">
          {historyLoading ? (
            <div className="p-6 text-center">
              <RefreshCw className="w-4 h-4 text-slate-300 animate-spin mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-5 text-center">
              <Clock className="w-6 h-6 text-slate-200 mx-auto mb-2" />
              <p className="text-[11px] text-slate-400">Belum ada riwayat pencarian.</p>
              <p className="text-[10px] text-slate-300 mt-0.5">Hasil pencarian akan muncul di sini.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50 max-h-64 overflow-y-auto">
              {filtered.map((h) => {
                const s = SOURCE_ICONS[h.source] || SOURCE_ICONS.google_maps;
                const Icon = s.icon;
                return (
                  <div
                    key={h._id}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/60 transition group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-7 h-7 rounded-lg ${s.bg} grid place-items-center shrink-0`}>
                        <Icon className={`w-3.5 h-3.5 ${s.color}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-slate-700 truncate">{h.keyword}</span>
                          <span className="text-[10px] text-slate-400">di</span>
                          <span className="text-xs font-medium text-slate-500 truncate">{h.city}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                          <span>{h.results} hasil</span>
                          {h.radius_km && <span>· {h.radius_km}km</span>}
                          <span>· {timeAgo(h.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {onRerun && (
                      <button
                        onClick={() => onRerun(h)}
                        className="text-[10px] font-semibold text-sky-500 hover:bg-sky-50 rounded-lg px-2 py-1 opacity-0 group-hover:opacity-100 transition"
                        title="Jalankan ulang pencarian ini"
                      >
                        <RefreshCw className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
